import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Col,
  FormControl,
  FormGroup,
  Row,
} from 'react-bootstrap';
import { observer } from 'mobx-react';
import DateTimePicker from 'react-datetime';
import './Ticket.css';
import Filters from '../../components/Filters/Filters';
import ReactTable from 'react-table';
import PaginationTable from '../../components/Pagination/PaginationTable';
import getSortHeaderProps from '../../utils/get-sorted-headers';
import { Link } from 'react-router-dom';
import { Loader } from '../../components/PageLoading/PageLoading';
import { withTranslation } from 'react-i18next';
import { useStores } from '../../hooks/use-stores';
import {
  displayDate,
  getLocaleName,
} from '../../utils/date';
import { OrderOrigin } from '../../constants/OrderOrigin';

const Ticket = observer((props) => {
  const { displayLocalTickets = false, t } = props;
  const { ticketStore, batchStore, newAuthStore, materialStore, settingsStore } = useStores();
  const [ loading, setLoading ] = useState(false);
  const [ filter, setFilter ] = useState('');
  const [ sorted, setSorted ] = useState([]);
  const [ selected, setSelected ] = useState([]);
  const { tickets, ticketsDateFrom, ticketsDateTo } = ticketStore;
  const { user } = newAuthStore;
  const isMounted = useRef(true);
  let dataToFilter = [];
  let _tickets = tickets.filter(v => displayLocalTickets ? v.origin === OrderOrigin.Local : v.origin !== OrderOrigin.Local);
  _tickets.forEach(ticket => {
    const mix = ticket.products?.find(p => p.isMix) || {};
    dataToFilter.push({
      ...ticket,
      _accountTypeName: ticket.accountType || '',
      _mixCode: mix.itemCode,
      _mixDescription: mix.itemDescription,
      _demo: ticket.isDemo ? 'demo' : 'real',
    });
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const onTicketClick = (ticket) => {
    const calculatedTicket = ticketStore.calculateTicketData(ticket, false);
    return props.onTicketClick(calculatedTicket);
  };

  const onVoidTicketClick = (ticket) => {
    const calculatedTicket = ticketStore.calculateTicketData(ticket, false);
    return props.onVoidTicketClick(calculatedTicket);
  };

  const getLogs = ticket => {
    return ticketStore.batchingLogs
      .filter(v => v.batchTicketId === ticket.id || v.batchingDate === `${ticket.loadStartTime}`);
  };

  const onLogClick = log => {
    setLoading(true);
    ticketStore.activeLog = log;
    batchStore.loadHistory = [];

    ticketStore.loadBatchingLog(log.fileName, (data) => {
      if (data) {
        batchStore.loadHistory = data;
      }
    }).finally(() => {
      if (isMounted.current) {
        setLoading(false);
      }
    });
  };

  const canTicketBeMerged = (ticket) => {
    const mix = ticket.products?.find(v => v.isMix) || {};
    return ticket.origin === OrderOrigin.Dispatch
      && mix
      && mix.quantity === mix.quantityInTruck;
  };

  const onMergeTicketClick = (ticket) => {
    return props.onMergeTicketClick(ticket);
  };

  const search = () => {
    setLoading(true);
    ticketStore.loadTickets()
      .finally(() => {
        if (isMounted.current) {
          setLoading(false);
        }
      });
  };

  const onChangeDate = fieldName => value => {
    ticketStore[fieldName] = value;
    search();
  };

  const validFrom = (date) => date.isSameOrBefore(ticketStore.ticketsDateTo);
  const validTo = (date) => date.isSameOrAfter(ticketStore.ticketsDateFrom);

  const handleChange = (e) => {
    const filter = e.target.value;
    setFilter(filter);
  };

  const getColumns = () => {
    const { timezone } = ticketStore;

    const getBatchedQuantity = (ticket) => {
      const mix = ticket.products?.find(v => v.isMix) || {};
      return mix.quantity;
    };

    const ticketNumberSequence = {
      headerText: t('ID'),
      accessor: ticket => ticket,
      Cell: ticket => (
        <span title={ticket.original.id}
          className={ticket.original.isVoided ? 'ticket-voided' : ticket.original.isDemo ? 'ticket-demo' : ''}>
          {ticket.original.number || ticket.original.id}
        </span>
      ),
      id: 'number',
      width: 70,
      className: 'sticky',
      headerClassName: 'sticky',
      ...getSortHeaderProps(sorted),
    };

    return [
      settingsStore.configuration.useTicketNumber ? ticketNumberSequence : {
        headerText: t('ID'),
        accessor: ticket => ticket,
        Cell: ticket => (
          <span data-e2e-id={`lbl-ticket-id-${ticket.original.id}`} title={ticket.original.id}
            className={ticket.original.isVoided ? 'ticket-voided' : ticket.original.isDemo ? 'ticket-demo' : ''}>
            {ticket.original.id}
          </span>
        ),
        id: 'id',
        width: 70,
        className: 'sticky',
        headerClassName: 'sticky',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Date'),
        accessor: ticket => ticket,
        Cell: ticket => (displayDate(ticket.original.loadStartTime, timezone, 'L', user.language)),
        id: 'loadStartTime',
        width: 90,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Time'),
        accessor: ticket => ticket,
        Cell: ticket => (displayDate(ticket.original.loadStartTime, timezone, 'LT', user.language)),
        id: 'loadStartTime',
        width: 70,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Ticket'),
        accessor: ticket => ticket,
        Cell: ticket => (<span>{ticket.original.dispatchTicketCode || ticket.original.id}</span>),
        width: 70,
        id: 'dispatchTicketCode',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Order Code'),
        accessor: ticket => ticket,
        Cell: ticket => (
          <span>
            {
              ticket.original.dispatchOrderCode
            || ticket.original.batchOrderCode
            || ticket.original.batchOrderId
            || ticket.original.code
            }
          </span>
        ),
        id: 'dispatchOrderCode',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Mix'),
        accessor: 'mixDesignCode',
        id: 'mixDesignCode',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Order Qty'),
        accessor: 'orderMixQuantity',
        id: 'orderMixQuantity',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Qty'),
        Cell: ticket => (<span>{getBatchedQuantity(ticket.original)}</span>),
        id: 'batchedQuantity',
        maxWidth: 50,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Truck'),
        accessor: 'truckCode',
        id: 'truckCode',
        maxWidth: 60,
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Driver'),
        accessor: 'driverName',
        id: 'driverName',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Customer'),
        accessor: 'customerName',
        id: 'customerName',
        ...getSortHeaderProps(sorted),
      },
      {
        headerText: t('Delivered to'),
        accessor: 'shipToAddress',
        id: 'shipToAddress',
        ...getSortHeaderProps(sorted),
      },
      {
        accessor: 'actions',
        width: 130,
        Cell: row => (
          <div className="text-right">
            {(canTicketBeMerged(row.original) || row.original.mergeBatchTicketId) && (
              <button className="btn btn-sm btn-default btn-action"
                onClick={(e) => {
                  e.stopPropagation();
                  onMergeTicketClick(row.original);
                }}>
                <i className={row.original.mergeBatchTicketId ? 'fa fa-unlink' : 'fa fa-link'} />
              </button>
            )}
            {getLogs(row.original).map((log, i) => (
              i === 0 && (
                <button className="btn btn-sm btn-default btn-action"
                  key={i}
                  disabled={batchStore.running.length && batchStore.canEmit}
                  onClick={(e) => {
                    e.stopPropagation();
                    onLogClick(log);
                  }}>
                  <i className="fa fa-download"/>
                </button>
              )
            ))}
            <Link className="btn btn-sm btn-default btn-action"
              to={`/tickets/${row.original.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onTicketClick(row.original);
              }}>
              <i className="fa fa-file-text-o"/>
            </Link>
            <button className="btn btn-sm btn-default btn-action"
              disabled={row.original.isVoided}
              onClick={(e) => {
                e.stopPropagation();
                onVoidTicketClick(row.original);
              }}>
              <i className="fa fa-remove"/>
            </button>
          </div>
        ),
      },
    ];
  };

  return (
    <Row className="ticket-mngt">
      <Col sm={12}>
        <Row>
          <Col sm={4}>
            <FormGroup>
              <FormControl
                data-e2e-id="inp-ticket-search"
                type="text"
                value={filter}
                placeholder={t('Search')}
                onChange={handleChange}
              />
            </FormGroup>
          </Col>
          <Col sm={8}>
            <div className="input-group input-large date-picker input-daterange pull-right">
              <DateTimePicker
                inputProps={{ className: 'form-control', placeholder: 'date from' }}
                onChange={onChangeDate('ticketsDateFrom')}
                value={ticketsDateFrom}
                timeFormat={true}
                closeOnSelect={true}
                isValidDate={validFrom}
                locale={getLocaleName(user.language)}
              />
              <span className="input-group-addon"> - </span>
              <DateTimePicker
                inputProps={{ className: 'form-control', placeholder: 'date to' }}
                onChange={onChangeDate('ticketsDateTo')}
                value={ticketsDateTo}
                timeFormat={true}
                closeOnSelect={true}
                isValidDate={validTo}
                locale={getLocaleName(user.language)}
              />
            </div>
          </Col>
        </Row>
        {(loading || !materialStore.materialDetailsLoaded) ? <Loader/> : (
          <Row>
            <Col sm={12}>
              <Filters filter={filter} data={dataToFilter} renderData={(tickets) => (
                <ReactTable
                  className="-highlight max-height-table"
                  data={tickets}
                  noDataText={' '}
                  columns={getColumns()}
                  sorted={sorted}
                  onSortedChange={sorted => setSorted(sorted)}
                  showPaginationTop={false}
                  showPaginationBottom={true}
                  showPageSizeOptions={true}
                  PaginationComponent={PaginationTable}
                  resizable={false}
                  minRows={1}
                  defaultPageSize={100}
                  getTrProps={(state, rowInfo) => {
                    if (rowInfo && rowInfo.row) {
                      return {
                        onClick: () => {
                          onTicketClick(rowInfo.original);
                          setSelected(rowInfo.index);
                        },
                        className: rowInfo.index === selected ? 'row-selected' : '',
                      };
                    }
                    return {};
                  }}
                />
              )}/>
            </Col>
          </Row>
        )}
      </Col>
    </Row>
  );
});

export default withTranslation()(Ticket);
